import { useEffect, useState } from "react";
import axios from "axios";
import { HeartHandshake, MessagesSquare, Sprout } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { DonateButton } from "@/components/DonateButton";
import { GalleryCard } from "@/components/GalleryCard";
import { YearFilter } from "@/components/YearFilter";
import { IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WellnessGallery = () => {
  const [items, setItems] = useState(null);
  const [year, setYear] = useState("All");

  const years = items
    ? ["All", ...[...new Set(items.map((i) => i.year))].sort().reverse()]
    : ["All"];
  const visible =
    items === null ? null : year === "All" ? items : items.filter((i) => i.year === year);

  useEffect(() => {
    axios
      .get(`${API}/gallery`, { params: { club: "wellbeing" } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  if (items !== null && items.length === 0) return null;

  return (
    <section className="bg-ember-subtle py-14" data-testid="wellbeing-gallery">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <Eyebrow>Wellness in action</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
            Moments from the programme
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-coal-muted">
            Awareness talks, teacher wellness sessions and honest conversations. Like, comment and
            share to show your support.
          </p>
        </Reveal>
        {items !== null && items.length > 0 && (
          <YearFilter years={years} active={year} onChange={setYear} count={visible?.length} testIdPrefix="wellness" />
        )}
        {visible === null ? (
          <p className="mt-10 text-sm text-coal-muted" data-testid="wellness-gallery-loading">Loading gallery...</p>
        ) : visible.length === 0 ? (
          <p className="mt-10 text-sm text-coal-muted" data-testid="wellness-gallery-empty">No moments from {year} yet.</p>
        ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((item, i) => (
              <GalleryCard key={item.id} item={item} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const PILLARS = [
  {
    icon: MessagesSquare,
    title: "Storytelling & conversation",
    text: "Meaningful conversations and personal development activities that encourage self-awareness and authentic expression.",
  },
  {
    icon: HeartHandshake,
    title: "Safe, supportive spaces",
    text: "Spaces where young people can share their experiences, find their voice and connect with others, without stigma.",
  },
  {
    icon: Sprout,
    title: "Resilience & growth",
    text: "Healthy coping skills and emotional wellbeing that help young people navigate adolescence and early adulthood.",
  },
];

export default function Wellbeing() {
  return (
    <div data-testid="wellbeing-page">
      <section className="relative overflow-hidden bg-ember grain py-16 sm:py-20">
        <div className="absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-gold/25 blur-[110px]" />
        <div className="relative mx-auto max-w-5xl px-6">
          <Reveal>
            <Eyebrow light>Wellbeing programme</Eyebrow>
            <h1 className="mt-5 font-display text-5xl font-extrabold tracking-tight text-sand sm:text-6xl lg:text-7xl">
              Your Story <span className="text-coal-dark">Matters</span>
            </h1>
            <p className="mt-7 max-w-3xl text-base leading-relaxed text-sand/90 md:text-lg">
              Community outreach that balances work and life, without forgetting to check in with
              yourself. Wellbeing equips learners and teachers with the coping skills and mechanisms
              to deal with the stressors of the classroom.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>A youth mental health initiative</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
              Being heard is a powerful step toward healing
            </h2>
            <p className="mt-6 text-base leading-relaxed text-coal-muted">
              Your Story Matters creates safe and supportive spaces where young people can share
              their experiences, find their voice, and connect with others. Through storytelling,
              meaningful conversations, and personal development activities, the programme encourages
              self-awareness, resilience, and emotional wellbeing while helping young people navigate
              the challenges of adolescence and early adulthood.
            </p>
            <p className="mt-5 text-base leading-relaxed text-coal-muted">
              We believe that every young person's story has value. By fostering connection, reducing
              the stigma around mental health, and empowering young people to express themselves
              authentically, Your Story Matters helps participants build confidence, develop healthy
              coping skills, and recognize their potential to create positive change in their own
              lives and communities.
            </p>
            <div className="mt-8">
              <DonateButton testId="wellbeing-donate-button">Support wellbeing</DonateButton>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-[2.5rem] rounded-bl-[6rem] shadow-[0_30px_70px_-30px_rgba(217,93,57,0.5)]">
                <img
                  src={IMAGES.wellbeing}
                  alt="Young people in a supportive conversation"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 rounded-2xl bg-coal px-6 py-4 text-sand shadow-xl">
                <span className="font-mono-brand text-[10px] font-bold uppercase tracking-[0.25em] text-ember-light">
                  Because
                </span>
                <p className="font-heading text-sm font-bold">every story has value</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mx-auto mt-24 grid max-w-7xl gap-5 px-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div
                className="h-full rounded-[2rem] border border-ember/15 bg-white p-8 transition-colors duration-500 hover:border-ember/40"
                data-testid={`wellbeing-pillar-${i}`}
              >
                <span className="inline-flex items-center justify-center rounded-2xl bg-ember-subtle p-3 text-ember">
                  <p.icon size={24} />
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-coal">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-coal-muted">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <WellnessGallery />
    </div>
  );
}
